import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, Search, User, LogOut, Headphones, Sparkles, Heart, X, Loader2, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "ACASĂ" },
  { to: "/tati-sofia", label: "DESPRE NOI" },
  { to: "/articole", label: "ARTICOLE" },
  { to: "/video", label: "VIDEO" },
  { to: "/comunitate", label: "POVEȘTI" },
  { to: "/petitii", label: "PETIȚII" },
  { to: "/intalniri", label: "ÎNTÂLNIRI" },
  { to: "/scan-ap", label: "SCAN AP" },
  { to: "/ajutor", label: "AJUTOR" },
  { to: "/legislatie", label: "LEGISLAȚIE" },
  { to: "/institutii", label: "INSTITUȚII" },
  { to: "/shop", label: "SHOP" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { user, logout } = useAuth() || {};
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSearchOpen(false); }, [loc.pathname]);

  // Cmd/Ctrl+K opens search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong" : "bg-[#0A1020]/95 backdrop-blur-md"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-24 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/" data-testid="header-logo" className="flex items-center">
            <img src={BRAND.logoMain} alt="Tati și Sofia" className="h-16 md:h-20 w-auto" />
          </Link>
          <Link
            to="/asistent"
            data-testid="header-left-chat"
            aria-label="Asistent AI"
            title="Asistent AI"
            className="hidden md:grid place-items-center w-9 h-9 rounded-sm text-[#F8F8F4]/85 hover:text-[#E89BB8] hover:bg-white/5 border border-white/10 ml-1"
          >
            <Sparkles className="w-4 h-4" />
          </Link>
          <Link
            to="/doneaza"
            data-testid="header-left-donate"
            aria-label="Susține această misiune"
            title="Susține această misiune"
            className="hidden md:grid place-items-center w-9 h-9 rounded-sm text-[#E89BB8] border border-[#E89BB8]/50 hover:bg-[#E89BB8]/10 hover:border-[#E89BB8] transition"
          >
            <Heart className="w-4 h-4 fill-[#E89BB8]" />
          </Link>
        </div>

        <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center">
          {NAV.map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.to.replaceAll("/", "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 text-[12px] font-semibold tracking-wider transition-colors relative ${
                  isActive ? "text-[#D4A63D]" : "text-[#F8F8F4]/85 hover:text-[#D4A63D]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && <span className="absolute -bottom-1 left-3 right-3 h-0.5 bg-[#D4A63D]" />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="header-search-btn"
            onClick={() => setSearchOpen(true)}
            aria-label="Caută pe site"
            className="hidden md:grid place-items-center w-9 h-9 rounded-sm text-[#F8F8F4]/85 hover:text-[#D4A63D] hover:bg-white/5"
          >
            <Search className="w-4 h-4" />
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-1">
              <Link to="/profil" data-testid="header-profile" className="text-[#F8F8F4]/85 hover:text-[#D4A63D] text-xs font-semibold tracking-wider px-2 py-2 flex items-center gap-1.5">
                <User className="w-4 h-4" /> {user.name?.split(" ")[0]?.toUpperCase()}
              </Link>
              <button data-testid="header-logout" onClick={async () => { await logout(); nav("/"); }} className="text-[#A0AABF] hover:text-[#C83A3A] p-2"><LogOut className="w-4 h-4" /></button>
            </div>
          ) : (
            <Link to="/autentificare" data-testid="header-login" className="hidden md:flex items-center gap-1.5 text-xs font-semibold tracking-wider text-[#F8F8F4]/85 hover:text-[#D4A63D] px-3 py-2">
              <User className="w-4 h-4" /> LOGIN
            </Link>
          )}

          <Link
            to="/programare" data-testid="header-cta-booking" className="hidden md:inline-flex items-center gap-2 px-4 lg:px-5 py-2.5 btn-gold rounded-sm text-[11px] font-bold tracking-wider">
            <Headphones className="w-4 h-4" />
            <span className="hidden lg:inline">VORBEȘTE CU GABRIEL</span>
            <span className="lg:hidden">GABRIEL</span>
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu" className="xl:hidden p-2 text-[#F8F8F4]" aria-label="Menu">
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] max-w-sm bg-[#0A1020] border-l border-white/10 p-0">
              <div className="flex flex-col h-full">
                <div className="px-6 py-5 border-b border-white/10">
                  <img src={BRAND.logoMain} alt="Tati și Sofia" className="h-14" />
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-4">
                  {NAV.map(n => (
                    <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => `block px-4 py-3 rounded-sm text-sm font-semibold tracking-wider ${isActive ? "text-[#D4A63D] bg-white/5" : "text-[#F8F8F4]/90 hover:bg-white/5"}`}>{n.label}</NavLink>
                  ))}
                  <div className="h-px bg-white/5 my-3" />
                  <NavLink to="/spune-povestea" className="block px-4 py-2.5 text-sm text-[#F8F8F4]/90 hover:bg-white/5">Spune povestea</NavLink>
                  <NavLink to="/asistent" className="block px-4 py-2.5 text-sm text-[#E89BB8] hover:bg-white/5 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Asistent AI</NavLink>
                  <NavLink to="/doneaza" className="block px-4 py-2.5 text-sm text-[#F8F8F4]/90 hover:bg-white/5">Susține misiunea</NavLink>
                  <NavLink to="/semnaleaza" className="block px-4 py-2.5 text-sm text-[#F8F8F4]/90 hover:bg-white/5">Semnalează</NavLink>
                  <NavLink to="/contact" className="block px-4 py-2.5 text-sm text-[#F8F8F4]/90 hover:bg-white/5">Contact</NavLink>
                </div>
                <div className="p-5 border-t border-white/10 space-y-2">
                  <Link to="/programare" className="block text-center px-4 py-3 btn-gold rounded-sm text-xs font-bold tracking-wider">VORBEȘTE CU GABRIEL</Link>
                  <Link to="/doneaza" data-testid="mobile-cta-donate" className="flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-xs font-bold tracking-wider border border-[#E89BB8]/50 text-[#E89BB8] hover:bg-[#E89BB8]/10">
                    <Heart className="w-4 h-4 fill-[#E89BB8]" /> SUSȚINE ACEASTĂ MISIUNE
                  </Link>
                  {user ? (
                    <button onClick={async () => { await logout(); nav("/"); }} className="w-full px-4 py-3 btn-ghost-light rounded-sm text-xs font-semibold tracking-wider">IEȘI DIN CONT</button>
                  ) : (
                    <Link to="/autentificare" className="block text-center px-4 py-3 btn-ghost-light rounded-sm text-xs font-semibold tracking-wider">LOGIN / ÎNREGISTRARE</Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </header>
  );
};

/* ============ SEARCH MODAL ============ */
const KIND_STYLES = {
  article: { color: "#D4A63D", bg: "bg-[#D4A63D]/10" },
  petition: { color: "#C83A3A", bg: "bg-[#C83A3A]/10" },
  event: { color: "#E89BB8", bg: "bg-[#E89BB8]/10" },
  video: { color: "#5FA0E8", bg: "bg-[#5FA0E8]/10" },
  story: { color: "#9C8FE0", bg: "bg-[#9C8FE0]/10" },
};

const SearchModal = ({ onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debounced, setDebounced] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    if (debounced.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    api.get("/search", { params: { q: debounced } })
      .then((r) => { if (alive) setResults(r.data?.results || []); })
      .catch(() => { if (alive) setResults([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [debounced]);

  const go = (url) => { onClose(); nav(url); };

  return (
    <div
      data-testid="search-modal"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="max-w-2xl mx-auto mt-24 mx-4 rounded-lg border border-[#D4A63D]/30 bg-[#0F1830] shadow-[0_20px_80px_rgba(0,0,0,0.6)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search className="w-4 h-4 text-[#D4A63D]" />
          <input
            data-testid="search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută articole, petiții, întâlniri, povești..."
            className="flex-1 bg-transparent outline-none text-white text-sm placeholder:text-[#6C7A9C]"
          />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-[#D4A63D]" />}
          <button data-testid="search-close" onClick={onClose} className="text-[#A0AABF] hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="search-results">
          {q.trim().length < 2 && (
            <div className="p-8 text-center text-[#6C7A9C] text-sm">
              Începe să scrii minim 2 litere pentru a căuta pe tot site-ul.
              <div className="mt-3 text-[10px] tracking-widest uppercase text-[#6C7A9C]/70">Scurtătură: Ctrl+K / ⌘K</div>
            </div>
          )}
          {q.trim().length >= 2 && !loading && results.length === 0 && (
            <div className="p-8 text-center text-[#6C7A9C] text-sm">
              Niciun rezultat pentru „<span className="text-white">{q}</span>”.
            </div>
          )}
          {results.map((r, i) => {
            const st = KIND_STYLES[r.kind] || { color: "#A0AABF", bg: "bg-white/5" };
            return (
              <button
                key={`${r.kind}-${i}`}
                data-testid={`search-result-${i}`}
                onClick={() => go(r.url)}
                className="w-full text-left px-3 py-3 rounded-md hover:bg-white/5 flex items-start gap-3 group"
              >
                <span
                  className={`text-[10px] tracking-widest uppercase font-bold px-2 py-0.5 rounded ${st.bg} shrink-0 mt-0.5`}
                  style={{ color: st.color }}
                >
                  {r.label}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-semibold group-hover:text-[#D4A63D] transition">{r.title}</div>
                  {r.snippet && <div className="text-[#A0AABF] text-xs mt-1 line-clamp-1">{r.snippet}</div>}
                </div>
                <ArrowRight className="w-4 h-4 text-[#6C7A9C] group-hover:text-[#D4A63D] shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
